import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import LoadingSpinner from '../components/LoadingSpinner';
import Toast from '../components/Toast';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1', sky: '#38BDF8',
  coral: '#FF6B6B', lavender: '#A78BFA', text: '#E8EDF5', sub: '#C5CDD8',
};

const PLAN_BADGE = {
  trial:      { label: 'Trial',      bg: 'rgba(240,180,41,0.15)',  color: C.gold },
  free:       { label: 'Trial',      bg: 'rgba(240,180,41,0.15)',  color: C.gold },
  basic:      { label: 'Basic',      bg: 'rgba(56,189,248,0.15)',  color: C.sky },
  growth:     { label: 'Growth',     bg: 'rgba(167,139,250,0.15)', color: C.lavender },
  enterprise: { label: 'Enterprise', bg: 'rgba(240,180,41,0.15)',  color: C.gold },
  pro:        { label: 'Pro',        bg: 'rgba(56,189,248,0.15)',  color: C.sky },
  business:   { label: 'Business',   bg: 'rgba(240,180,41,0.15)',  color: C.gold },
};

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats]       = useState(null);
  const [users, setUsers]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [pages, setPages]       = useState(1);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const [planFilter, setPlan]   = useState('');
  const [loading, setLoading]   = useState(true);
  const [toast, setToast]       = useState(null);

  useEffect(() => {
    // redirect non-admins
    if (user && user.email !== import.meta.env.VITE_ADMIN_EMAIL && user.email !== 'augustineeinstein@gmail.com') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    api.get('/admin/stats').then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { page };
    if (search) params.search = search;
    if (planFilter) params.plan = planFilter;
    api.get('/admin/users', { params })
      .then((r) => { setUsers(r.data.users); setTotal(r.data.total); setPages(r.data.pages); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, search, planFilter]);

  const changePlan = async (id, plan) => {
    try {
      await api.put(`/admin/users/${id}/plan`, { plan });
      setUsers((u) => u.map((x) => (x.id === id ? { ...x, plan } : x)));
      if (stats) {
        setStats((s) => ({ ...s })); // trigger a re-fetch stats
        api.get('/admin/stats').then((r) => setStats(r.data)).catch(() => {});
      }
      setToast({ message: 'Plan updated', type: 'success' });
    } catch {
      setToast({ message: 'Failed to update plan', type: 'error' });
    }
  };

  const fmt = (n) => `$${Number(n).toLocaleString()}`;

  const selectStyle = { background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', color: C.text, fontSize: '0.875rem', fontFamily: 'inherit' };

  return (
    <div style={{ minHeight: '100vh', background: C.ink, color: C.text, padding: 32, fontFamily: "'Cabinet Grotesk', sans-serif" }}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
          <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: `1px solid ${C.border}`, color: C.muted, padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'inherit' }}>← Dashboard</button>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>Admin Panel</h1>
          <span style={{ background: C.coral, color: C.ink, fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>RESTRICTED</span>
        </div>

        {/* ── Stats ── */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 32 }}>
            {[
              { label: 'Total Users',        value: stats.totalUsers },
              { label: 'Trial Users',         value: stats.usersByPlan.trial,    color: C.gold },
              { label: 'Pro Users',           value: stats.usersByPlan.pro,      color: C.sky },
              { label: 'Business Users',      value: stats.usersByPlan.business, color: C.gold },
              { label: 'Est. MRR',            value: fmt(stats.estimatedMRR),    color: C.teal },
              { label: 'New This Week',       value: stats.newSignupsThisWeek,   color: C.lavender },
              { label: 'Total Sales',         value: stats.totalSalesProcessed },
            ].map((s) => (
              <div key={s.label} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '18px 20px' }}>
                <div style={{ fontSize: '0.72rem', color: C.muted, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
                <div style={{ fontSize: '1.6rem', fontWeight: 800, color: s.color || C.text }}>{s.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Users Table ── */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, marginBottom: 28 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: 16 }}>Users</h2>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <input
              placeholder="Search name or email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              style={{ ...selectStyle, flex: 1, minWidth: 200 }}
            />
            <select value={planFilter} onChange={(e) => { setPlan(e.target.value); setPage(1); }} style={selectStyle}>
              <option value="">All Plans</option>
              <option value="trial">Trial</option>
              <option value="basic">Basic</option>
              <option value="growth">Growth</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>

          {loading ? <LoadingSpinner message="Loading users..." /> : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                      {['Name', 'Email', 'Plan', 'Joined', 'Change Plan'].map((h) => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: C.muted, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => {
                      const badge = PLAN_BADGE[u.plan] || PLAN_BADGE.trial;
                      return (
                        <tr key={u.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: '10px 12px', fontWeight: 500 }}>{u.name}</td>
                          <td style={{ padding: '10px 12px', color: C.muted }}>{u.email}</td>
                          <td style={{ padding: '10px 12px' }}>
                            <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: badge.bg, color: badge.color }}>{badge.label}</span>
                          </td>
                          <td style={{ padding: '10px 12px', color: C.muted }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                          <td style={{ padding: '10px 12px' }}>
                            <select value={u.plan} onChange={(e) => changePlan(u.id, e.target.value)}
                              style={{ background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: 7, padding: '5px 10px', color: C.text, fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                              <option value="trial">Trial</option>
                              <option value="basic">Basic</option>
                              <option value="growth">Growth</option>
                              <option value="enterprise">Enterprise</option>
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16, alignItems: 'center' }}>
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                    style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: C.muted, padding: '5px 12px', borderRadius: 7, cursor: 'pointer', opacity: page === 1 ? 0.4 : 1, fontFamily: 'inherit' }}>←</button>
                  <span style={{ fontSize: '0.85rem', color: C.muted }}>Page {page} of {pages} ({total} users)</span>
                  <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages}
                    style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: C.muted, padding: '5px 12px', borderRadius: 7, cursor: 'pointer', opacity: page === pages ? 0.4 : 1, fontFamily: 'inherit' }}>→</button>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── System Status ── */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: 16 }}>System Status</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            {[
              { label: 'API', value: 'v1 Active', ok: true },
              { label: 'Database', value: 'Connected', ok: true },
              { label: 'Email', value: 'Resend', ok: true },
              { label: 'Payments', value: 'Paystack', ok: true },
            ].map((s) => (
              <div key={s.label} style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: C.muted, marginBottom: 2 }}>{s.label}</div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>{s.value}</div>
                </div>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: 'rgba(0,201,177,0.15)', color: C.teal }}>✓</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
